/**
 * The tab strip of the wide layout (refonte modulaire §9.1; maquette
 * `OngletHistorique.dc.html`).
 *
 * §9.1 gives the experience's editor three tabs - Timeline, Persona,
 * Historique - and the third one is the layer's: « l'historique, "utilisé
 * dans" et le retrait passent dans ce troisième onglet ». So this file draws
 * the strip and nothing else; which panel a tab shows is decided by
 * `versioned-editor.tsx`, and the tabs an object declares are its own.
 *
 * ## A `nav` with `aria-current`, and NOT an ARIA tablist
 *
 * The selected tab travels in the URL (`?tab=`), so a reload, a back button or
 * a pasted link land on the same panel. That makes each tab a DESTINATION
 * rather than a widget state, and `aria-current="page"` is what says so - the
 * same mark the rail and the Réglages bar already carry. An ARIA tablist would
 * promise the arrow-key régime of a widget whose panels do not exist until
 * their tab is chosen, and would say « tab 2 of 3 » about something the browser
 * history walks.
 *
 * ## Buttons and not links, for the same reason `onView` is a callback
 *
 * The layer is blind to the route it sits on: TanStack types `to` and `search`
 * against the route tree, so a `<Link>` written here could not typecheck
 * against the params of whichever screen mounts it. The selection therefore
 * leaves by a callback and the CONSUMER writes the navigation, exactly as
 * `?version=` already does through `onView` / `onCloseView`.
 *
 * The label is « Sections » and not « Sections de l'expérience » of the
 * maquette: the noun would have to be templated with its article, and
 * `VersionedWording` refuses that by construction (« un article et un accord ne
 * sont pas une concaténation »). Nothing else on the screen is a `nav` but the
 * admin rail, which names itself « Administration ».
 */

export interface VersionedTabItem {
  /** What `?tab=` carries: « timeline ». */
  readonly id: string;
  /** What the strip shows: « Timeline ». */
  readonly label: string;
}

export interface VersionedTabsProps {
  /** The `data-testid` prefix of the object: « experience » (`VersionedWording.entity`). */
  entity: string;
  /** The object's own tabs, then « Historique » - the order they are read in. */
  tabs: readonly VersionedTabItem[];
  /** The id of the tab being shown, already resolved against the list. */
  active: string;
  onSelect: (id: string) => void;
}

/** Everything a tab wears whatever its state - and nothing either state sets. */
const TAB_BASE =
  'inline-flex h-10 cursor-pointer items-center border-b-[3px] px-1 font-semibold text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const TAB_OFF = 'border-transparent text-gray-600 hover:text-bleu-nuit';
const TAB_ON = 'border-bleu-nuit text-bleu-nuit';

export function VersionedTabs({ entity, tabs, active, onSelect }: VersionedTabsProps) {
  return (
    <nav
      aria-label="Sections"
      data-testid={`${entity}-onglets`}
      className="border-gray-200 border-b"
    >
      <ul className="-mb-px flex flex-wrap gap-6">
        {tabs.map((tab) => {
          const current = tab.id === active;
          return (
            <li key={tab.id}>
              <button
                type="button"
                data-testid={`${entity}-onglet-${tab.id}`}
                // `page` and not `true`: the panel it opens is addressable, and
                // `true` is what a step or a date in a set would carry.
                aria-current={current ? 'page' : undefined}
                onClick={() => onSelect(tab.id)}
                className={`${TAB_BASE} ${current ? TAB_ON : TAB_OFF}`}
              >
                {tab.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
